import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Search, Menu, X, Gamepad2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = ({ date, title, gameName = "Daily Pocket Grids", heroTitle, introText }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Format date nicely (e.g., "Sunday, Jan 4")
  const formattedDate = new Date(date).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <>
      {/* Top Navigation Bar - Floating Card Style */}
      <div className="fixed top-4 left-0 right-0 z-40 px-3 md:px-4 pointer-events-none w-full">
        <div className="max-w-6xl mx-auto bg-white/90 backdrop-blur-md border border-gray-200/50 shadow-sm rounded-2xl pointer-events-auto">
          <div className="flex items-center justify-between px-3 md:px-5 py-3">
             <Link to="/" className="flex items-center gap-2 md:gap-3 group">
               <div className="w-9 h-9 bg-brand-blue rounded-xl flex items-center justify-center text-white font-black text-xl shadow-sm rotate-3 transform transition-transform group-hover:rotate-0">
                 P
               </div>
               <div className="flex flex-col leading-none">
                  <span className="font-bold text-lg text-gray-900 tracking-tight group-hover:text-brand-blue transition-colors">PocketHints</span>
                  <span className="text-sm font-bold text-brand-blue">Daily Reddit Puzzle Helper</span>
               </div>
             </Link>
             
             {/* Desktop Navigation */}
             <div className="hidden sm:flex items-center gap-2">
               {/* Games Dropdown */}
               <div className="relative group">
                 <button className="flex items-center gap-2 px-3 py-2 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-lg transition-colors">
                   <span>Games</span>
                   <Menu className="w-4 h-4" />
                 </button>
                 <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-100 rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="p-2 flex flex-col gap-1">
                      <Link to="/pocket-grids" className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-blue/5 hover:text-brand-blue rounded-lg font-bold">Pocket Grids</Link>
                      <Link to="/syllo" className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-blue/5 hover:text-brand-blue rounded-lg font-bold">Syllo</Link>
                      <Link to="/hot-and-cold" className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-blue/5 hover:text-brand-blue rounded-lg font-bold">Hot and Cold</Link>
                      <Link to="/byrdle" className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-blue/5 hover:text-brand-blue rounded-lg font-bold">Byrdle</Link>
                      <div className="h-px bg-gray-100 my-1"></div>
                      <Link to="/daily-guess-solver" className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-blue/5 hover:text-brand-blue rounded-lg font-bold">DailyGuess Solver</Link>
                    </div>
                 </div>
               </div>

               <div className="h-4 w-px bg-gray-200 mx-1"></div>

               <div className="relative group">
                 <button className="flex items-center gap-2 px-3 py-2 text-sm font-bold text-brand-blue hover:bg-brand-blue/5 rounded-lg transition-colors">
                   <span>Today's Hints</span>
                   <Menu className="w-4 h-4" />
                 </button>
                 <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-100 rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="p-2 flex flex-col gap-1">
                      <Link to="/todays-pocket-grids-clues-answers" className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-blue/5 hover:text-brand-blue rounded-lg font-bold">Pocket Grids</Link>
                      <Link to="/todays-syllo-clues-answers" className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-blue/5 hover:text-brand-blue rounded-lg font-bold">Syllo</Link>
                      <Link to="/todays-hot-and-cold-clues-answers" className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-blue/5 hover:text-brand-blue rounded-lg font-bold">Hot and Cold</Link>
                      <Link to="/todays-byrdle-clues-answers" className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-blue/5 hover:text-brand-blue rounded-lg font-bold">Byrdle</Link>
                    </div>
                 </div>
               </div>

               <Link to="/reddit-games" className="flex items-center gap-2 px-3 py-2 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-lg transition-colors">
                 <Gamepad2 className="w-4 h-4" />
                 <span>Reddit Games</span>
               </Link>

               <Link to="/archives" className="flex items-center gap-2 px-3 py-2 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-lg transition-colors">
                 <Calendar className="w-4 h-4" />
                 <span>Archives</span>
               </Link>
               
               <Link to="/search" className="p-2 text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-lg transition-colors" aria-label="Search">
                 <Search className="w-5 h-5" />
               </Link>

               <div className="h-4 w-px bg-gray-200 mx-1"></div>

               <a 
                 href="https://tally.so/r/rj6Jpp" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-brand-blue hover:bg-blue-600 rounded-lg transition-colors shadow-sm"
               >
                 <span>Become a Sponsor</span>
               </a>
             </div>

             {/* Mobile Menu Button */}
             <button 
               onClick={toggleMenu}
               className="sm:hidden p-2 text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-lg transition-colors"
               aria-label="Menu"
             >
               {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
             </button>
          </div>

          {/* Mobile Navigation Dropdown */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden border-t border-gray-100 sm:hidden"
              >
                <div className="flex flex-col p-2 gap-1">
                  <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Games & Tools</div>
                  <Link 
                    to="/pocket-grids" 
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-xl transition-colors"
                  >
                    <span>Pocket Grids Guide</span>
                  </Link>
                  <Link 
                    to="/syllo" 
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-xl transition-colors"
                  >
                    <span>Syllo Guide</span>
                  </Link>
                  <Link 
                    to="/hot-and-cold" 
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-xl transition-colors"
                  >
                    <span>Hot and Cold Guide</span>
                  </Link>
                  <Link 
                    to="/byrdle" 
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-xl transition-colors"
                  >
                    <span>Byrdle Guide</span>
                  </Link>
                  <Link 
                    to="/daily-guess-solver" 
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-xl transition-colors"
                  >
                    <span>DailyGuess Solver</span>
                  </Link>

                  <div className="border-t border-gray-100 my-1"></div>
                  <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Today's Puzzles</div>
                  <Link 
                    to="/todays-pocket-grids-clues-answers" 
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-xl transition-colors"
                  >
                    <span>Pocket Grids Hints</span>
                  </Link>
                  <Link 
                    to="/todays-syllo-clues-answers" 
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-xl transition-colors"
                  >
                    <span>Syllo</span>
                  </Link>
                  <Link 
                    to="/todays-hot-and-cold-clues-answers" 
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-xl transition-colors"
                  >
                    <span>Hot and Cold</span>
                  </Link>
                  <Link 
                    to="/todays-byrdle-clues-answers" 
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-xl transition-colors"
                  >
                    <span>Byrdle</span>
                  </Link>
                  <Link 
                    to="/reddit-games"  
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-xl transition-colors"
                  >
                    <Gamepad2 className="w-5 h-5" />
                    <span>Reddit Games</span>
                  </Link>
                  <Link 
                    to="/archives"  
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-xl transition-colors"
                  >
                    <Calendar className="w-5 h-5" />
                    <span>Archives</span>
                  </Link>
                  <Link 
                    to="/search" 
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:text-brand-blue hover:bg-brand-blue/5 rounded-xl transition-colors"
                  >
                    <Search className="w-5 h-5" />
                    <span>Search</span>
                  </Link>

                  <div className="border-t border-gray-100 my-1"></div>
                  <a 
                    href="https://tally.so/r/rj6Jpp" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-brand-blue hover:bg-brand-blue/5 rounded-xl transition-colors"
                  >
                    <span>Become a Sponsor</span>
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Spacing for fixed header */}
      <div className="h-24"></div>

      <header className="pt-8 pb-10 px-4 max-w-4xl mx-auto text-center relative">
        {/* Decorative background blur behind title */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-brand-blue/5 rounded-full blur-3xl -z-10"></div>

        {/* Top Meta Bar - Pill Design */}
        <div className="inline-flex items-center gap-2 mb-6 bg-white border border-gray-200 rounded-full pl-1 pr-4 py-1 shadow-sm hover:border-brand-blue/50 transition-colors cursor-default">
           <div className="bg-brand-yellow text-yellow-900 rounded-full p-1.5">
             <Calendar className="w-3.5 h-3.5" />
           </div>
           <span className="text-sm font-bold text-gray-600">{formattedDate}</span>
           <span className="w-1 h-1 rounded-full bg-gray-300 mx-1"></span>
           <span className="text-sm font-medium text-gray-500">{title}</span>
        </div>

        {/* Main Title - Serif & Large */}
        <h1 className="text-4xl md:text-6xl font-black text-gray-900 tracking-tight mb-6 leading-tight">
          {heroTitle ? (
            <span className="relative inline-block text-brand-blue">
              {heroTitle}
              <svg className="absolute w-full h-3 -bottom-1 left-0 text-brand-yellow -z-10 opacity-60" viewBox="0 0 100 10" preserveAspectRatio="none">
                 <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
              </svg>
            </span>
          ) : gameName === "PocketHints Helper Hub" ? (
             <>
               The Ultimate <span className="relative inline-block text-brand-blue">
                 Puzzle Helper
                 <svg className="absolute w-full h-3 -bottom-1 left-0 text-brand-yellow -z-10 opacity-60" viewBox="0 0 100 10" preserveAspectRatio="none">
                    <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
                 </svg>
               </span> <br/> for Daily Games
             </>
          ) : gameName === "Daily Hot and Cold" ? (
             <>
               Crack today's <span className="relative inline-block text-brand-blue">
                 Hot and Cold
                 <svg className="absolute w-full h-3 -bottom-1 left-0 text-brand-yellow -z-10 opacity-60" viewBox="0 0 100 10" preserveAspectRatio="none">
                    <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
                 </svg>
               </span> <br/> Daily Puzzle
             </>
          ) : gameName === "Daily Byrdle" ? (
             <>
               Crack today's <span className="relative inline-block text-brand-blue">
                 Byrdle
                 <svg className="absolute w-full h-3 -bottom-1 left-0 text-brand-yellow -z-10 opacity-60" viewBox="0 0 100 10" preserveAspectRatio="none">
                    <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
                 </svg>
               </span> <br/> Daily Puzzle
             </>
          ) : (
            <>
              Solve the <span className="relative inline-block text-brand-blue">
                {gameName}
                <svg className="absolute w-full h-3 -bottom-1 left-0 text-brand-yellow -z-10 opacity-60" viewBox="0 0 100 10" preserveAspectRatio="none">
                   <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
                </svg>
              </span> <br/> without the struggle.
            </>
          )}
        </h1>

        {/* Intro Text */}
        <div className="max-w-xl mx-auto text-lg text-gray-600 leading-relaxed">
          {introText !== undefined ? introText : (
              <>
                Progressive hints that nudge you in the right direction. <br className="hidden md:block" />
                Reveal definitions and anagrams before the answer.
              </>
          )}
        </div>

      </header>
    </>
  );
};

export default Header;
